/**
 * @fileoverview
 * This file exports all the routes in the application in the following format:
 * ```js
 * {
 *    path: '/absolute/path/to/route', // notice the leading slash '/' at the beginning of the path
 *    name: 'a-unique-name-for-the-route',
 * }
 * ```
 *
 * This is useful for referencing routes (or their names) anywhere in the application without having to hard-code the paths.
 */

export const ROOT = {
    path: '/',
    name: 'home',
};

export const FORGOT_PASSWORD = {
    path: '/forgot-password',
    name: 'forgot-password',
};

export const RESET_PASSWORD = {
    path: '/reset-password',
    name: 'reset-password',
};

export const RESET_CONFIRMATION = {
    path: '/reset-confirmation',
    name: 'reset-confirmation',
};

export const EMPLOYER_SIGNUP = {
    path: '/signup',
    name: 'signup',
};

export const VERIFICATION_CODE = {
    path: '/verification-code',
    name: 'verification-code',
};

export const ENTERPRISE_ONBOARDING_ACCOUNT = {
    path: '/enterprise/onboarding/account',
    name: 'enterprise-onboarding-account',
};

export const ENTERPRISE_ONBOARDING_PLANS = {
    path: '/enterprise/onboarding/plans',
    name: 'enterprise-onboarding-plans',
};

export const ENTERPRISE_ONBOARDING_SERVICE_AGREEMENT = {
    path: '/enterprise/onboarding/service-agreement/:id',
    name: 'enterprise-onboarding-service-agreement',
};

export const EMPLOYER_SIGNIN = {
    path: '/signin',
    name: 'signin',
};

const ACCOUNT_PREFIX = '/account';
export const ACCOUNT_ROOT = {
    path: ACCOUNT_PREFIX,
    name: 'account',
};

export const TALENT_DETAILS = {
    path: `${ACCOUNT_PREFIX}/talent/details/:id`,
    name: 'account-details',
};

export const ACCOUNT_DASHBOARD = {
    path: `${ACCOUNT_PREFIX}/dashboard`,
    name: 'account-dashboard',
};

export const ACCOUNT_SEARCH = {
    path: `${ACCOUNT_PREFIX}/search`,
    name: 'account-search',
};

const ACCOUNT_SETTINGS_PREFIX = `${ACCOUNT_PREFIX}/settings`;
export const ACCOUNT_SETTINGS = {
    path: ACCOUNT_SETTINGS_PREFIX,
    name: 'account-settings',
};

export const PAYMENT_METHOD = {
    path: `${ACCOUNT_SETTINGS_PREFIX}/payment-method`,
    name: 'account-settings-payment-method',
};

export const COMPANY_INFO = {
    path: `${ACCOUNT_SETTINGS_PREFIX}/company-info`,
    name: 'account-settings-company-info',
};

export const ACCOUNT_TEAM = {
    path: `${ACCOUNT_SETTINGS_PREFIX}/team`,
    name: 'account-settings-team',
};

export const ACCOUNT_DOWNLOADS = {
    path: `${ACCOUNT_SETTINGS_PREFIX}/downloads`,
    name: 'account-settings-downloads',
};

export const JOBS_ROUTE_PREFIX = '/jobs';
export const JOBS = {
    path: JOBS_ROUTE_PREFIX,
    name: 'jobs',
};

export const JOB_DETAILS = {
    path: `${JOBS_ROUTE_PREFIX}/details/:id`,
    name: 'job-details',
};

export const ARCHIVED_JOBS = {
    path: `${JOBS_ROUTE_PREFIX}/archived`,
    name: 'archived-jobs',
};

export const ARCHIVED_JOB_DETAILS = {
    path: `${JOBS_ROUTE_PREFIX}/archived/details/:id`,
    name: 'archived-job-details',
};

export const ADD_JOB = {
    path: `${JOBS_ROUTE_PREFIX}/add`,
    name: 'add-job',
};

export const EDIT_JOB = {
    path: `${JOBS_ROUTE_PREFIX}/edit/:id`,
    name: 'edit-job',
};

export const EDIT_SMART_JOB = {
    path: `${JOBS_ROUTE_PREFIX}/smart/edit/:id`,
    name: 'edit-smart-job',
};

export const ADD_SMART_JOB = {
    path: `${JOBS_ROUTE_PREFIX}/add/smart`,
    name: 'add-smart-job',
};

export const PROMOTE_JOB = {
    path: `${JOBS_ROUTE_PREFIX}/promote/:id`,
    name: 'promote-job',
};

export const PROMOTE_SMART_JOB = {
    path: `${JOBS_ROUTE_PREFIX}/smart/promote/:id`,
    name: 'promote-smart-job',
};

export const TALENT_PIPELINES_ROUTE_PREFIX = '/pipelines';
export const TALENT_PIPELINES = {
    path: TALENT_PIPELINES_ROUTE_PREFIX,
    name: 'talent-pipelines',
};

export const ADD_TALENT_PIPELINE = {
    path: `${TALENT_PIPELINES.path}/add`,
    name: 'add-new-talent-pipeline',
};

export const EDIT_TALENT_PIPELINE = {
    path: `${TALENT_PIPELINES.path}/:id/edit`,
    name: 'edit-talent-pipeline',
};

export const TALENT_PIPELINE_DETAILS = {
    path: `${TALENT_PIPELINES.path}/:id/details`,
    name: 'talent-pipeline-details',
};

export const ARCHIVED_TALENT_PIPELINES = {
    path: `${TALENT_PIPELINES_ROUTE_PREFIX}/archived`,
    name: 'archived-talent-pipelines',
};

export const ARCHIVED_TALENT_PIPELINE_DETAILS = {
    path: `${ARCHIVED_TALENT_PIPELINES.path}/:id/details`,
    name: 'archived-talent-pipeline-details',
};

const QUICK_PROMOTE_PREFIX = '/promote';
export const QUICK_PROMOTE = {
    path: QUICK_PROMOTE_PREFIX,
    name: 'quick-promote-job',
};

export const TALENT_PROFILE = {
    path: '/talent/profile',
    name: 'talent-profile',
};

const TALENT_ONBOARDING_PREFIX = '/talent/onboarding';
export const TALENT_ONBOARDING = {
    path: TALENT_ONBOARDING_PREFIX,
    name: 'onboarding',
};

export const ONBOARDING_UPLOAD_RESUME = {
    path: `${TALENT_ONBOARDING_PREFIX}/uploadresume`,
    name: 'onboarding-uploadresume',
};

export const ONBOARDING_CREATE_ACCOUNT = {
    path: `${TALENT_ONBOARDING_PREFIX}/createaccount`,
    name: 'onboarding-create-account',
};

export const ONBOARDING_VERIFICATION_CODE = {
    path: `${TALENT_ONBOARDING_PREFIX}/verificationcode`,
    name: 'talent-onboarding-verification-code',
};

const TALENT_ACTIVATION_PREFIX = '/talent/activation';
export const TALENT_ACTIVATION = {
    path: `${TALENT_ACTIVATION_PREFIX}/activate`,
    name: 'talent-activation',
};

export const ACTIVATION_GET_STARTED = {
    path: `${TALENT_ACTIVATION_PREFIX}/get-started`,
    name: 'activation-get-started',
};

export const TALENT_ACTIVATION_INFO = {
    path: `${TALENT_ACTIVATION_PREFIX}/info`,
    name: 'activation-info',
};

export const TALENT_LOGIN = {
    path: '/talent/login',
    name: 'onboarding-login',
};

export const TALENT_FORGOT_PASSWORD = {
    path: '/talent/forgot-password',
    name: 'onboarding-forgot-password',
};

export const TALENT_VERIFICATION_CODE = {
    path: '/talent/codereset',
    name: 'talent-password-reset-verification-code',
};

export const TALENT_PASSWORD_RESET = {
    path: '/talent/passwordreset',
    name: 'onboarding-password-reset',
};
